import { lazy } from 'react';
import { ConviteConversaPage } from './pages/ConviteConversaPage.js';
import { Redirect, Route, Switch } from 'wouter';
import { StagePreview } from './components/stage/StagePreview.js';
import { IS_FULL } from './lib/edition.js';
import { useStageOn } from './lib/stage/useStageOn.js';

// Sec Full: a página do WhatsApp só existe nessa edição (ver edition.ts). A condição
// usa a variável de ambiente crua (não o IS_FULL importado) para o Vite trocá-la por
// um literal e descartar o import dinâmico — nada do WhatsApp vai para os outros pacotes.
const WhatsAppPage =
  import.meta.env.VITE_EDITION === 'full'
    ? lazy(() => import('./pages/full/WhatsAppPage.js').then((m) => ({ default: m.WhatsAppPage })))
    : null;
import { clinicRoutes } from './clinicRoutes.js';
export {
  ClinicaPublicaPage,
  ConfirmarConsultaPage,
  PagarCobrancaPage,
  FormularioPublicoPage,
} from './clinicRoutes.js';
import { AgendaPage } from './pages/agenda/AgendaPage.js';
import { AgendaRotinaPage } from './pages/AgendaRotinaPage.js';
import { AjustesPage } from './pages/AjustesPage.js';
import { AtalhosInicioPage } from './pages/AtalhosInicioPage.js';
const ApagarContaPage = lazy(() =>
  import('./pages/conta/ApagarContaPage.js').then((m) => ({ default: m.ApagarContaPage })),
);
const IdiomasPage = lazy(() =>
  import('./pages/IdiomasPage.js').then((m) => ({ default: m.IdiomasPage })),
);
const IdiomaPage = lazy(() =>
  import('./pages/IdiomasPage.js').then((m) => ({ default: m.IdiomaPage })),
);
const SuaIaPage = lazy(() =>
  import('./pages/conta/SuaIaPage.js').then((m) => ({ default: m.SuaIaPage })),
);
const TelasPage = lazy(() =>
  import('./pages/conta/TelasPage.js').then((m) => ({ default: m.TelasPage })),
);
const ContaAvisosPage = lazy(() =>
  import('./pages/conta/AvisosPage.js').then((m) => ({ default: m.AvisosPage })),
);
const DonoPage = lazy(() => import('./pages/DonoPage.js').then((m) => ({ default: m.DonoPage })));
const SegurancaPage = lazy(() =>
  import('./pages/conta/SegurancaPage.js').then((m) => ({ default: m.SegurancaPage })),
);
const JeitoDeFalarPage = lazy(() =>
  import('./pages/conta/JeitoDeFalarPage.js').then((m) => ({ default: m.JeitoDeFalarPage })),
);
const PrivacidadeContaPage = lazy(() =>
  import('./pages/conta/PrivacidadeContaPage.js').then((m) => ({
    default: m.PrivacidadeContaPage,
  })),
);
import { AparelhosPage } from './pages/AparelhosPage.js';
import { LembretesPage } from './pages/LembretesPage.js';
import { AssistenteCelularPage } from './pages/AssistenteCelularPage.js';
import { AprovacoesPage } from './pages/AprovacoesPage.js';
import { ContatoDetalhePage } from './pages/ContatoDetalhePage.js';
import { ContatosPage } from './pages/ContatosPage.js';
import { HabilidadePage } from './pages/HabilidadePage.js';
import { JeitoUnicoPage } from './pages/JeitoUnicoPage.js';
import { MudarJeitoPage } from './pages/MudarJeitoPage.js';
import { PerfilPage } from './pages/PerfilPage.js';
import { PlanoPage } from './pages/PlanoPage.js';
import { SobreVocePage } from './pages/SobreVocePage.js';

// O mensageiro (SDK + criptografia WASM) só carrega quando entra em Conversas.
const legalPages = () => import('./pages/LegalPages.js');
export const PrivacidadePage = lazy(() =>
  legalPages().then((m) => ({ default: m.PrivacidadePage })),
);
export const TermosPage = lazy(() => legalPages().then((m) => ({ default: m.TermosPage })));
export const ExcluirContaPage = lazy(() =>
  legalPages().then((m) => ({ default: m.ExcluirContaPage })),
);
export const AjudaPage = lazy(() => legalPages().then((m) => ({ default: m.AjudaPage })));
const SuportePage = lazy(() =>
  import('./pages/SuportePage.js').then((m) => ({ default: m.SuportePage })),
);
export const TermoNegocioPage = lazy(() =>
  legalPages().then((m) => ({ default: m.TermoNegocioPage })),
);
const PessoaPage = lazy(() =>
  import('./pages/PessoaPage.js').then((m) => ({ default: m.PessoaPage })),
);
const AtaPage = lazy(() => import('./pages/AtaPage.js').then((m) => ({ default: m.AtaPage })));
const AvisosPage = lazy(() =>
  import('./pages/AvisosPage.js').then((m) => ({ default: m.AvisosPage })),
);
const ClientesPage = lazy(() =>
  import('./pages/ClientesPage.js').then((m) => ({ default: m.ClientesPage })),
);
const ClientePage = lazy(() =>
  import('./pages/ClientesPage.js').then((m) => ({ default: m.ClientePage })),
);
const ServicosPage = lazy(() =>
  import('./pages/ServicosPage.js').then((m) => ({ default: m.ServicosPage })),
);
const EntregasPage = lazy(() =>
  import('./pages/DocumentosPage.js').then((m) => ({ default: m.DocumentosPage })),
);
const LixeiraPage = lazy(() =>
  import('./pages/LixeiraPage.js').then((m) => ({ default: m.LixeiraPage })),
);
const GuardadoPage = lazy(() =>
  import('./pages/GuardadoPage.js').then((m) => ({ default: m.GuardadoPage })),
);
const ControlePage = lazy(() =>
  import('./pages/ControlePage.js').then((m) => ({ default: m.ControlePage })),
);
const TelaItemPage = lazy(() =>
  import('./pages/TelaItemPage.js').then((m) => ({ default: m.TelaItemPage })),
);
const EntregaPage = lazy(() =>
  import('./pages/EntregasPage.js').then((m) => ({ default: m.EntregaPage })),
);
const ObjetivosPage = lazy(() =>
  import('./pages/ObjetivosPage.js').then((m) => ({ default: m.ObjetivosPage })),
);
const ObjetivoPage = lazy(() =>
  import('./pages/ObjetivoPage.js').then((m) => ({ default: m.ObjetivoPage })),
);
const ReceitasPage = lazy(() =>
  import('./pages/ReceitasPage.js').then((m) => ({ default: m.ReceitasPage })),
);
const ReceitaPage = lazy(() =>
  import('./pages/ReceitaPage.js').then((m) => ({ default: m.ReceitaPage })),
);
const PertoPage = lazy(() =>
  import('./pages/perto/PertoPage.js').then((m) => ({ default: m.PertoPage })),
);
const PertoCategoriaPage = lazy(() =>
  import('./pages/perto/PertoCategoriaPage.js').then((m) => ({ default: m.PertoCategoriaPage })),
);
const TarefaPage = lazy(() =>
  import('./pages/perto/TarefaPage.js').then((m) => ({ default: m.TarefaPage })),
);
const LugarPage = lazy(() =>
  import('./pages/perto/LugarPage.js').then((m) => ({ default: m.LugarPage })),
);
const PlanoNegocioPage = lazy(() =>
  import('./pages/negocio/PlanoNegocioPage.js').then((m) => ({ default: m.PlanoNegocioPage })),
);
const LojaPage = lazy(() =>
  import('./pages/loja/LojaPage.js').then((m) => ({ default: m.LojaPage })),
);
/** Página de uma loja da vitrine do bairro (`/v/<id>`), sem conta (G3). */
export const VitrineBairroPage = lazy(() =>
  import('./pages/vitrine/VitrineBairroPage.js').then((m) => ({ default: m.VitrineBairroPage })),
);
const ProfissionaisPage = lazy(() =>
  import('./pages/pros/ProfissionaisPage.js').then((m) => ({ default: m.ProfissionaisPage })),
);
const ProfissionalPage = lazy(() =>
  import('./pages/pros/ProfissionalPage.js').then((m) => ({ default: m.ProfissionalPage })),
);
const GolpePage = lazy(() =>
  import('./pages/GolpePage.js').then((m) => ({ default: m.GolpePage })),
);
const EmailPage = lazy(() =>
  import('./pages/EmailPage.js').then((m) => ({ default: m.EmailPage })),
);
const AssumirProPage = lazy(() =>
  import('./pages/perto/AssumirProPage.js').then((m) => ({ default: m.AssumirProPage })),
);
const AssumirVitrinePage = lazy(() =>
  import('./pages/vitrine/AssumirVitrinePage.js').then((m) => ({ default: m.AssumirVitrinePage })),
);
/** Confirmar o horário pelo link do lembrete de um negócio (G1), sem conta. */
export const ConfirmarVisitaPage = lazy(() =>
  import('./pages/negocio/ConfirmarVisitaPage.js').then((m) => ({
    default: m.ConfirmarVisitaPage,
  })),
);
export const VitrinePage = lazy(() =>
  import('./pages/loja/VitrinePage.js').then((m) => ({ default: m.VitrinePage })),
);
const PedidoPage = lazy(() =>
  import('./pages/loja/PedidoPage.js').then((m) => ({ default: m.PedidoPage })),
);
const EnvioPage = lazy(() =>
  import('./pages/envios/EnvioPage.js').then((m) => ({ default: m.EnvioPage })),
);
const EnviosPage = lazy(() =>
  import('./pages/envios/EnvioPage.js').then((m) => ({ default: m.EnviosPage })),
);
const PedidosPage = lazy(() =>
  import('./pages/loja/PedidoPage.js').then((m) => ({ default: m.PedidosPage })),
);
const NegocioPage = lazy(() =>
  import('./pages/NegocioPage.js').then((m) => ({ default: m.NegocioPage })),
);
/** secretarios.net/<nome>: a página pública do negócio pelo nome curto. */
export const NomePublicoPage = lazy(() =>
  import('./pages/NomePublicoPage.js').then((m) => ({ default: m.NomePublicoPage })),
);
export const AtendimentoPage = lazy(() =>
  import('./pages/AtendimentoPage.js').then((m) => ({ default: m.AtendimentoPage })),
);
const PeopleStatic = lazy(() =>
  import('./components/stage/PeoplePanel.js').then((m) => ({ default: m.PeopleStatic })),
);
const ConversasPage = lazy(() =>
  import('./pages/ConversasPage.js').then((m) => ({ default: m.ConversasPage })),
);
const SecretarioPage = lazy(() =>
  import('./pages/SecretarioPage.js').then((m) => ({ default: m.SecretarioPage })),
);
const ConversaPage = lazy(() =>
  import('./pages/ConversaPage.js').then((m) => ({ default: m.ConversaPage })),
);

/** A Início: o palco para quem já tem a liberação; a de sempre para os demais. */
// A Início é o palco (montado pelo esqueleto); aqui fica só o retrato parado que o
// deslizar de voltar desenha por baixo de uma página.
function HomeRoute() {
  return <StagePreview />;
}
// Com o palco, Conversas e Contatos vivem na folha de Pessoas (montada pelo
// esqueleto); aqui fica só a folha parada que o deslizar de voltar desenha.
function ConversasRoute() {
  return useStageOn() ? <PeopleStatic segment="conversas" /> : <ConversasPage />;
}
function ContatosRoute() {
  return useStageOn() ? <PeopleStatic segment="contatos" /> : <ContatosPage />;
}

/** A Equipe como página (com o palco, ela sai do trilho lateral da Início). */

/**
 * Telas do app com sessão aberta. Fica num componente à parte porque o gesto
 * de voltar desenha a tela de destino por cima, com estas mesmas rotas.
 */
export function AppRoutes() {
  return (
    <Switch>
      <Route path="/" component={HomeRoute} />
      <Route path="/habilidade/:id" component={HabilidadePage} />
      <Route path="/conversas" component={ConversasRoute} />
      <Route path="/conversas/secretario" component={SecretarioPage} />
      <Route path="/conversas/convite/:contactId" component={ConviteConversaPage} />
      <Route path="/conversas/:roomId" component={ConversaPage} />
      <Route path="/contatos" component={ContatosRoute} />
      <Route path="/contatos/:contactId" component={ContatoDetalhePage} />
      <Route path="/pessoas/:username" component={PessoaPage} />
      <Route path="/agenda" component={AgendaPage} />
      <Route path="/agenda/rotina" component={AgendaRotinaPage} />
      <Route path="/aprovacoes" component={AprovacoesPage} />
      <Route path="/ajustes" component={AjustesPage} />
      <Route path="/ajustes/inicio" component={AtalhosInicioPage} />
      <Route path="/ajustes/avisos" component={ContaAvisosPage} />
      <Route path="/ajustes/seguranca" component={SegurancaPage} />
      <Route path="/dono" component={DonoPage} />
      <Route path="/ajustes/telas" component={TelasPage} />
      <Route path="/email" component={EmailPage} />
      <Route path="/profissionais" component={ProfissionaisPage} />
      <Route path="/profissionais/:id" component={ProfissionalPage} />
      <Route path="/ajustes/sua-ia" component={SuaIaPage} />
      {/* Controle no celular para a TV ou o notebook em exibição. */}
      <Route path="/controle" component={ControlePage} />
      <Route path="/idiomas" component={IdiomasPage} />
      <Route path="/idiomas/:lang" component={IdiomaPage} />
      <Route path="/ajustes/privacidade" component={PrivacidadeContaPage} />
      <Route path="/ajustes/secretario" component={JeitoDeFalarPage} />
      <Route path="/ajustes/apagar" component={ApagarContaPage} />
      <Route path="/aparelhos" component={AparelhosPage} />
      {IS_FULL && WhatsAppPage && <Route path="/whatsapp" component={WhatsAppPage} />}
      <Route path="/lembretes" component={LembretesPage} />
      <Route path="/assistente-do-celular" component={AssistenteCelularPage} />
      <Route path="/perfil" component={PerfilPage} />
      <Route path="/sobre-voce" component={SobreVocePage} />
      <Route path="/mudar-jeito" component={MudarJeitoPage} />
      <Route path="/jeito-unico" component={JeitoUnicoPage} />
      <Route path="/plano" component={PlanoPage} />
      <Route path="/negocio" component={NegocioPage} />
      <Route path="/negocio/clientes" component={ClientesPage} />
      <Route path="/negocio/loja" component={LojaPage} />
      <Route path="/negocio/plano" component={PlanoNegocioPage} />
      <Route path="/negocio/avisos" component={AvisosPage} />
      <Route path="/negocio/clientes/:contactId" component={ClientePage} />
      {/* Clínica e Saúde (fase 7): fora do pacote da loja, ver clinicRoutes.tsx. */}
      {clinicRoutes()}
      <Route path="/servicos" component={ServicosPage} />
      <Route path="/entregas" component={EntregasPage} />
      <Route path="/entregas/lixeira" component={LixeiraPage} />
      <Route path="/entregas/guardado/:id" component={GuardadoPage} />
      {/* O que a tela grande montou, para ver no celular ou mandar de novo para a TV. */}
      <Route path="/tela-grande/:id" component={TelaItemPage} />
      <Route path="/entregas/exemplo/:service" component={EntregaPage} />
      {/* Atas das reuniões já feitas continuam legíveis como documento. */}
      <Route path="/entregas/ata/:id" component={AtaPage} />
      <Route path="/entregas/:id" component={EntregaPage} />
      <Route path="/objetivos" component={ObjetivosPage} />
      <Route path="/objetivos/:id" component={ObjetivoPage} />
      <Route path="/receitas" component={ReceitasPage} />
      <Route path="/receitas/:id" component={ReceitaPage} />
      <Route path="/perto" component={PertoPage} />
      <Route path="/perto/c/:categoria" component={PertoCategoriaPage} />
      <Route path="/perto/lugar/:id/assumir" component={AssumirProPage} />
      <Route path="/perto/lugar/:id" component={LugarPage} />
      <Route path="/perto/tarefa/:id" component={TarefaPage} />
      <Route path="/a/:username/confirmar/:token" component={ConfirmarVisitaPage} />
      <Route path="/a/:username" component={AtendimentoPage} />
      <Route path="/loja/:username" component={VitrinePage} />
      <Route path="/v/:placeId/assumir" component={AssumirVitrinePage} />
      <Route path="/v/:placeId" component={VitrineBairroPage} />
      <Route path="/envios" component={EnviosPage} />
      <Route path="/envios/:id" component={EnvioPage} />
      <Route path="/pedidos" component={PedidosPage} />
      <Route path="/pedidos/:id" component={PedidoPage} />
      <Route path="/reuniao/:id">
        {(params) => <Redirect to={`/entregas/ata/${params.id}`} replace />}
      </Route>
      <Route path="/termos" component={TermosPage} />
      <Route path="/termo-negocio" component={TermoNegocioPage} />
      <Route path="/privacidade" component={PrivacidadePage} />
      <Route path="/excluir-conta" component={ExcluirContaPage} />
      <Route path="/ajuda" component={SuportePage} />
      <Route path="/ajuda/perguntas" component={AjudaPage} />
      <Route path="/ajuda/golpe" component={GolpePage} />
      {/* Por último: o nome curto de um negócio; o que não for de ninguém volta para a tela inicial. */}
      <Route path="/:nome">
        {() => <NomePublicoPage fallback={<Redirect to="/" replace />} />}
      </Route>
      <Route>
        <Redirect to="/" replace />
      </Route>
    </Switch>
  );
}
